// CancelSubscriptionPage — where cancelling happens, and what it does to
// your data. It does NOT cancel anything.
//
// This page used to end subscriptions. It stopped on 2026-09-10 (Owner
// ruling), and the removal is the point rather than a simplification: a
// customer who cancelled here and not in the marketplace that sold them the
// subscription went on being billed, with nothing anywhere to say the two
// disagreed. Two places to cancel is two states somebody then reconciles by
// hand, and the reconciliation only ever starts after somebody notices.
//
// What survives is the part that was actually load-bearing. Of the three
// things offboarding.py says must be true when a customer leaves, the button
// was only ever the first — and the other two are still here:
//
// 1. THE CHANNEL DECIDES WHEN BILLING STOPS. Now stated as the whole of the
//    instruction rather than as a caveat under a confirm box: cancel where
//    you bought it. There is nothing here to press, so nothing here can
//    imply the bill has stopped.
//
// 2. THE LAST HOUR STILL GETS BILLED, so deployments keep running for a
//    while after cancelling. Shown, with the reason it is that date, so
//    nobody reads a still-running instance as a billing error.
//
// 3. THE CUSTOMER IS TOLD WHAT HAPPENS TO THEIR DATA, IN WRITING. Rendered
//    verbatim from the server — it is a constant in the code precisely so it
//    is identical for every customer, and this page must not paraphrase it
//    and does not hold a copy to paraphrase.
//
// An ending recorded by the channel is shown when there is one. The page
// reports; it never creates.
import { useCallback } from 'react';
import { ArrowLeft, TriangleAlert } from 'lucide-react';
import { Button } from '@miq/design-system/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@miq/design-system/components/ui/card';
import { Separator } from '@miq/design-system/components/ui/separator';

import { StateBadge } from '../components/StateBadge';
import { when } from '../data/instants';
import { usePortalSession } from '../auth/context';
import { fetchCancellationFlow } from '../data/subscriptions';
import { useLoaded } from '../data/useLoaded';
import { useDocumentTitle } from '../../lib/documentTitle';

/** Seconds as the hours or days a person reads, for the flush window. */
function windowInWords(seconds: number): string {
  const hours = Math.round(seconds / 3600);
  if (hours < 48) {
    return `${hours} hour${hours === 1 ? '' : 's'}`;
  }
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'}`;
}

export interface CancelSubscriptionPageProps {
  publicId: string;
  onBack: () => void;
}

export function CancelSubscriptionPage({
  publicId,
  onBack,
}: CancelSubscriptionPageProps) {
  useDocumentTitle('End subscription');
  const { authorizedFetch } = usePortalSession();
  const read = useCallback(
    () => fetchCancellationFlow(authorizedFetch, publicId),
    [authorizedFetch, publicId],
  );
  const { state } = useLoaded(read);

  if (state.status === 'loading') {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-8 text-sm text-muted-foreground">
            Reading what ending this would do…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (state.status === 'refused') {
    return (
      <div className="space-y-4 p-6">
        <Button variant="ghost" size="sm" onPress={onBack}>
          <ArrowLeft className="size-3.5" /> Back
        </Button>
        <Card>
          <CardContent className="py-8">
            <p role="alert" className="text-sm text-destructive">
              {state.refusal}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { subscription, cancellation, wind_down: windDown } = state.data;

  return (
    <div className="space-y-4 p-6">
      <Button variant="ghost" size="sm" onPress={onBack}>
        <ArrowLeft className="size-3.5" /> Back to the subscription
      </Button>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <TriangleAlert className="size-4 text-warning" />
            Ending this subscription
          </CardTitle>
          <CardDescription className="flex items-center gap-2">
            <span className="font-mono text-xs">
              {subscription.agreement_ref}
            </span>
            <StateBadge subscription={subscription} />
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 text-sm">
          {cancellation ? (
            <div className="space-y-2">
              <p className="font-medium">This subscription is ending.</p>
              <p className="text-muted-foreground">
                Your deployments keep running until{' '}
                {when(cancellation.wind_down_not_before)}, so the last of
                your usage can be reported. Billing ends when{' '}
                {subscription.channel} says it does.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              <p className="font-medium">
                Cancel where you bought it — not here.
              </p>
              <p className="text-muted-foreground">
                {/* The whole instruction, not a caveat under a button.
                    There is nothing on this page to press, so nothing
                    on it can imply the bill has stopped. */}
                This subscription was sold through {subscription.channel},
                and that is the only place it can be ended. Cancel it there
                and we are told automatically — there is no step to
                complete with us, and nothing you do here would stop your
                bill.
              </p>
            </div>
          )}

          <Separator />

          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              When your deployments stop
            </div>
            <p className="text-muted-foreground">
              Not immediately, and not by accident. {subscription.channel}{' '}
              accepts usage for{' '}
              {windowInWords(windDown.flush_window_seconds)} after a
              cancellation
              {windDown.declared_by_channel
                ? ''
                : ' (it declares no window of its own, so we allow a generous default)'}
              , and your deployments keep running until that closes so the
              last of your usage can be billed correctly.
            </p>
            {windDown.instances.length > 0 && (
              <ul className="ml-4 list-disc text-muted-foreground">
                {windDown.instances.map((instance) => (
                  <li key={instance.public_id}>{instance.name}</li>
                ))}
              </ul>
            )}
          </div>

          <Separator />

          <div className="space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              What happens to your data
            </div>
            {/* Verbatim from the server. Not paraphrased here, and no copy
                held here to paraphrase — it is one statement, identical for
                every customer, and it is the record of what they were told. */}
            <p className="whitespace-pre-line text-muted-foreground">
              {state.data.retention_statement}
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
